import "whatwg-fetch"; // Polyfill for Fetch for IE.

import * as React from "react";

import difference from "lodash.difference";
import * as querystring from "querystring";

import { DataService } from "./data/DataService";
import { Errors } from "./Errors";

import { IAlert, IAlertResponse, isIAlertResponse } from "./classes/Alert";
import { isIText, IText } from "./classes/Text";

import { ModalHtmlClassHack } from "../common/modalHtmlClassHack/ModalHtmlClassHack";
import { UnifiedPublishingModal } from "./UnifiedPublishingModal";
import { UnifiedPublishingUnminimizeButton } from "./UnifiedPublishingUnminimizeButton";

import { SessionStorage } from "./sessionStorage/SessionStorage";

import { EnviroConfig } from "../EnviroConfig";

import "../../../node_modules/font-awesome/scss/font-awesome.scss"; // Font-Awesome SASS CSS sheet, not included in final build.
import "../common/styles/presenceFramework.css"; // Not included in final build.
import "./UnifiedPublishing.css";

export interface IUnifiedPublishingAlertProps extends React.Props<UnifiedPublishingAlert> {
  portletInstanceID: number;
}

export interface IUnifiedPublishingAlertState {
  error: string;
  liveAlerts: IAlert[];
  displayModal: boolean;
  modalIsMinimized: boolean;
  numAjaxRequests: number;
  text: IText;
}

export default class UnifiedPublishingAlert extends React.Component<IUnifiedPublishingAlertProps, IUnifiedPublishingAlertState> {

  private dataService: DataService;

  public constructor(props: IUnifiedPublishingAlertProps) {
    super(props);

    this.setInitialState();
    this.dataService = new DataService();

    this.onMinimizeModal = this.onMinimizeModal.bind(this);
    this.onUnminimizeModal = this.onUnminimizeModal.bind(this);
  }

  public componentDidMount() {
    this.loadLiveAlerts();
  }

  public onMinimizeModal(event) {
    event.preventDefault();
    SessionStorage.setModalAsMinimized();
    this.setState({ modalIsMinimized: true });
  }

  public onUnminimizeModal(event) {

    event.preventDefault();

    this.loadLiveAlerts(true);

    SessionStorage.setModalAsUnminimized();
    this.setState({ modalIsMinimized: false });
  }

  public render() {

    const modalIsOpen = this.state.displayModal && !this.state.modalIsMinimized;

    return (
      <React.Fragment>

        <ModalHtmlClassHack modalIsOpen={modalIsOpen} />

        {modalIsOpen &&
          <UnifiedPublishingModal
            displayModal={this.state.displayModal}
            liveAlerts={this.state.liveAlerts}
            modalIsMinimized={this.state.modalIsMinimized}
            numAjaxRequests={this.state.numAjaxRequests}
            text={this.state.text}
            onMinimizeModal={this.onMinimizeModal}
          />
        }

        <UnifiedPublishingUnminimizeButton
          displayModal={this.state.displayModal}
          modalIsMinimized={this.state.modalIsMinimized}
          numAlerts={this.state.liveAlerts.length}
          text={this.state.text}
          onUnminimizeModal={this.onUnminimizeModal}
        />

        {/*<Errors errorIntro="An error occurred while retrieving Unified Publishing Alerts" errorMessage={this.state.error} />*/}

      </React.Fragment>
    );
  }

  private setInitialState() {
    const error = "";
    const liveAlerts: IAlert[] = [];
    const displayModal = false;
    const text = this.getBlankInternationalizedText();
    const modalIsMinimized = SessionStorage.getIfModalIsMinimized();
    const numAjaxRequests = SessionStorage.getNumAjaxRequests();

    this.state = {
      error,
      liveAlerts,
      displayModal,
      modalIsMinimized,
      numAjaxRequests,
      text,
    };
  }

  private getBlankInternationalizedText(): IText {
    const text: IText = {
      alerts: "",
      errorsHaveOccurred: "",
      loading: "",
      minimize: "",
      new: " ", // leave set as " " (space)! Used as flag whether int text has been loaded, if changed will cause New alert icons not to show on UnifiedPublishingLiveAlertDetail
      noAlerts: "",
      noAlertsDescription: "",
    };
    return text;
  }

  private loadLiveAlerts(isReload: boolean = false): void {

    this.addOneAJAXCallToCount();

    const serviceMethodName = "GetActiveAlerts";

    const returnDummyAlerts = this.getWhetherToShowDummyAlert();
    const params = JSON.stringify({ returnDummyAlerts });

    this.dataService.fetchWithTimeoutRace(serviceMethodName, params)
      .then((response) => {
        return this.dataService.handleResponseErrors(response, serviceMethodName);
      })
      .then((response) => {
        this.subtractOneAJAXCallFromCount();
        return response.json(); // json() returns a Promise, now need for that Promise to resolve:
      })
      .then((json) => {
        this.handleReturnedAlerts(json, isReload);
      }).catch((error) => {
        this.addError(error.message);
        this.subtractOneAJAXCallFromCount();
      });
  }

  private getWhetherToShowDummyAlert = (): boolean => {
    let showAlert = false;
    let theLocation = location.search;
    if (theLocation && theLocation.length > 0 && theLocation[0] === "?") { theLocation = theLocation.substring(1, theLocation.length); }
    const querystringObj = querystring.parse(theLocation);
    if (querystringObj && (querystringObj.showAlert || querystringObj.showAlerts)) {
      if (querystringObj.showAlert === "true" || querystringObj.showAlerts === "true") {
        showAlert = true;
      }
    }
    return showAlert;
  }

  private handleReturnedAlerts(json: any, isReload: boolean) {

    // .NET services wrap response in a 'd' property:
    if (json.d !== undefined) {
      json = json.d;
    }

    // Get the current live alert IDs.
    const liveAlerts = this.convertToIAlert(json);
    const liveAlertIDs: string[] = [];
    for (const thisAlert of liveAlerts) { liveAlertIDs.push(thisAlert.alertId); }

    // Check whether to unminimise the modal in the case that new alerts have arrived since it was minimised.
    const previousLiveAlertIDs = SessionStorage.getLiveAlertIDs();
    if (previousLiveAlertIDs.length > 0) {
      if (difference(liveAlertIDs, previousLiveAlertIDs).length > 0) {
        this.setState({ modalIsMinimized: false });
      }
    } else {
      if (liveAlerts.length > 0) {
        this.setState({ modalIsMinimized: false });
      }
    }

    // Update the locally stored live alert IDs.
    SessionStorage.setLiveAlertIDs(liveAlertIDs);

    // Decide whether modal+uniminimise button should appear at all.
    let displayModal = true;
    if (!isReload) {
      displayModal = liveAlerts.length > 0;
    }

    // Only request internationalised text from service if there is at least one active alert, and this is not a re-opening of the minimised alerts modal.
    if (!isReload && liveAlerts.length > 0) {
      this.loadInternationalizedText();
    }

    this.setState({ liveAlerts, displayModal });
  }

  private convertToIAlert(response: any): IAlert[] {
    const alerts = [];

    if (!Array.isArray(response)) {
      throw new Error("Alerts response is not an array.");
    }

    for (const thisResponseAlert of response) {
      // Verify data returned by service matches IText interface;
      if (isIAlertResponse(thisResponseAlert)) {
        const newAlert: IAlert = {
          alertId: thisResponseAlert.AlertId,
          alertType: thisResponseAlert.AlertType,
          body: thisResponseAlert.Body,
          expiryDate: new Date(thisResponseAlert.Expiry),
          title: thisResponseAlert.Title,
        };
        alerts.push(newAlert);
      } else {
        throw new Error("Alert data for an alert is not valid, missing expected fields.");
      }
    }

    return alerts;
  }

  private loadInternationalizedText(): void {

    this.addOneAJAXCallToCount();

    const serviceMethodName = "GetInternationalizedText";

    this.dataService.fetchWithTimeoutRace(serviceMethodName, null)
      .then((response) => {
        return this.dataService.handleResponseErrors(response, serviceMethodName);
      })
      .then((response) => {
        this.subtractOneAJAXCallFromCount();
        return response.json(); // json() returns a Promise, now need for that Promise to resolve:
      })
      .then((json) => {
        // .NET services wrap response in a 'd' property:
        if (json.d !== undefined) {
          json = json.d;
        }
        if (isIText(json)) { // Verify data returned by service matches IText interface;
          const text: IText = json;
          this.setState({ text });
        } else {
          this.addError("Internationalised text data returned by service is incomplete.");
        }
      }).catch((error) => {
        this.addError(error.message);
        this.subtractOneAJAXCallFromCount();
      });

  }

  private addOneAJAXCallToCount() {
    SessionStorage.incrementNumAjaxRequests();
    this.setState({ numAjaxRequests: SessionStorage.getNumAjaxRequests() });
  }

  private subtractOneAJAXCallFromCount() {
    SessionStorage.decrementNumAjaxRequests();
    this.setState({ numAjaxRequests: SessionStorage.getNumAjaxRequests() });
  }

  private addError(error: string) {
    this.setState({ error });
  }

}
